import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    title: 'Gobernanza corporativa y estructuras de decisión',
    description: 'Diseñamos e instalamos consejos y comités que aportan claridad y orden al proceso de decisión. Alineamos intereses, roles y estrategia para que la empresa avance con rumbo claro.',
    image: '/assets/service-governance.jpg',
  },
  {
    title: 'Legal corporativo y arquitectura jurídica',
    description: 'Construimos estatutos, contratos y estructuras societarias a la medida, asegurando protección y flexibilidad. Brindamos asesoría legal especializada ante retos y oportunidades corporativas.',
    image: '/assets/service-legal.jpg',
  },
  {
    title: 'Sistema familia–empresa–patrimonio',
    description: 'Definimos reglas y roles que armonizan la vida familiar con la operación del negocio y la gestión del patrimonio. Reducimos conflictos y preservamos la riqueza familiar a lo largo de generaciones.',
    image: '/assets/service-family.jpg',
  },
  {
    title: 'Secretaría corporativa estratégica',
    description: 'Administramos actas, agendas y seguimiento de acuerdos para que cada órgano de gobierno funcione con rigor. Esta disciplina institucional da continuidad y confianza en la toma de decisiones.',
    image: '/assets/service-secretary.jpg',
  },
  {
    title: 'Sucesión y continuidad del liderazgo',
    description: 'Planeamos el relevo de liderazgo y la transmisión de control accionario y patrimonial. Acompañamos a las familias para evitar vacíos de poder y preparar a las próximas generaciones.',
    image: '/assets/service-succession.jpg',
  },
  {
    title: 'Gestión patrimonial y family office',
    description: 'Estructuramos fideicomisos, holdings y vehículos que blindan los activos de la familia. Coordinamos asesores y planes de inversión para que el patrimonio crezca y se conserve.',
    image: '/assets/service-wealth.jpg',
  },
];

export default function ServicesSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      // Header
      gsap.from('.services-label', {
        y: 30, opacity: 0, duration: 0.8, ease: 'expo.out',
        scrollTrigger: { trigger: section, start: 'top 80%' },
      });
      gsap.from('.services-heading', {
        y: 40, opacity: 0, duration: 0.8, ease: 'expo.out', delay: 0.1,
        scrollTrigger: { trigger: section, start: 'top 80%' },
      });

      // Service rows
      section.querySelectorAll('.service-row').forEach((row, i) => {
        const img = row.querySelector('.service-img');
        const text = row.querySelector('.service-text');
        const isEven = i % 2 === 1;

        if (img) {
          gsap.from(img, {
            x: isEven ? 60 : -60,
            opacity: 0,
            duration: 1,
            ease: 'expo.out',
            scrollTrigger: { trigger: row, start: 'top 85%' },
          });
        }
        if (text) {
          gsap.from(text, {
            x: isEven ? -40 : 40,
            opacity: 0,
            duration: 1,
            ease: 'expo.out',
            delay: 0.2,
            scrollTrigger: { trigger: row, start: 'top 85%' },
          });
        }
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="servicios"
      className="relative w-full bg-[#f5f0e8] py-32 md:py-40"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-20 md:mb-28">
          <span className="services-label block font-mono text-[11px] uppercase tracking-[0.2em] text-[#C9A26E] mb-5">
            Servicios
          </span>
          <div className="flex items-end justify-between">
            <h2 className="services-heading font-heading text-[#1a1a1a] text-[clamp(28px,4vw,52px)] leading-[1.1] tracking-[-0.01em]">
              Áreas de especialidad
            </h2>
            <div className="hidden md:block w-24 h-px bg-[#C9A26E]/40 mb-4" />
          </div>
        </div>

        {/* Services */}
        <div className="space-y-24 md:space-y-32">
          {services.map((service, i) => {
            const isEven = i % 2 === 1;
            return (
              <div
                key={i}
                className="service-row grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
              >
                {/* Image */}
                <div className={`${isEven ? 'md:order-2 md:col-span-7' : 'md:col-span-7'}`}>
                  <div className="service-img relative overflow-hidden group">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </div>
                    {/* Subtle overlay on hover */}
                    <div className="absolute inset-0 bg-[#0f1419]/0 group-hover:bg-[#0f1419]/10 transition-colors duration-500" />
                    {/* Number badge */}
                    <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.15em] text-[#f0ebe2]/80 bg-[#0f1419]/60 backdrop-blur-sm px-3 py-1.5">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                  </div>
                </div>

                {/* Text */}
                <div className={`service-text ${isEven ? 'md:order-1 md:col-span-5' : 'md:col-span-5'}`}>
                  <div className="w-8 h-px bg-[#C9A26E] mb-6" />
                  <h3 className="font-heading text-[#1a1a1a] text-xl md:text-2xl leading-[1.3] mb-5">
                    {service.title}
                  </h3>
                  <p className="font-body text-[#6b6560] text-sm leading-[1.8]">
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
